export interface SpeciesSeoFaqDraft {
    question: string;
    answer: string;
}

export function readSpeciesSeoFaq(raw: unknown): SpeciesSeoFaqDraft[] {
    let value = raw;
    if (typeof value === 'string') {
        try {
            value = JSON.parse(value);
        } catch {
            return [];
        }
    }
    if (!Array.isArray(value)) return [];
    return value
        .map((item) => ({
            question: String((item as { question?: unknown })?.question ?? ''),
            answer: String((item as { answer?: unknown })?.answer ?? ''),
        }))
        .filter((item) => item.question.trim() || item.answer.trim());
}

/** Filas vacías no se guardan. Array vacío borra el FAQ en la RPC. */
export function speciesSeoFaqForSave(faqs: SpeciesSeoFaqDraft[]): SpeciesSeoFaqDraft[] {
    return faqs
        .map((item) => ({ question: item.question.trim(), answer: item.answer.trim() }))
        .filter((item) => item.question || item.answer);
}

interface SpeciesSeoFieldsProps {
    seoTitle: string;
    seoDescription: string;
    faqs: SpeciesSeoFaqDraft[];
    onSeoTitleChange: (value: string) => void;
    onSeoDescriptionChange: (value: string) => void;
    onFaqsChange: (faqs: SpeciesSeoFaqDraft[]) => void;
}

export function SpeciesSeoFields({
    seoTitle,
    seoDescription,
    faqs,
    onSeoTitleChange,
    onSeoDescriptionChange,
    onFaqsChange,
}: SpeciesSeoFieldsProps) {
    const updateFaq = (index: number, patch: Partial<SpeciesSeoFaqDraft>) => {
        onFaqsChange(faqs.map((item, i) => (i === index ? { ...item, ...patch } : item)));
    };

    return (
        <div className="rounded-xl border border-border bg-card/50 p-6 shadow-sm space-y-4">
            <h3 className="text-base font-semibold text-foreground border-b border-border pb-2">
                SEO del Atlas
            </h3>
            <p className="text-xs text-muted-foreground">
                Si el título o la meta se dejan vacíos, la ficha pública usa la plantilla neutra.
                El FAQ se muestra en la página y como FAQPage aunque haya una sola pregunta.
            </p>
            <div>
                <label className="block text-sm font-medium text-foreground mb-1" htmlFor="species-seo-title">
                    Título SEO ({seoTitle.trim().length}/60)
                </label>
                <input
                    id="species-seo-title"
                    type="text"
                    value={seoTitle}
                    maxLength={60}
                    onChange={(e) => onSeoTitleChange(e.target.value)}
                    placeholder="Lubina: ficha y cómo se pesca"
                    className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm"
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-foreground mb-1" htmlFor="species-seo-description">
                    Meta descripción ({seoDescription.trim().length}/155)
                </label>
                <textarea
                    id="species-seo-description"
                    value={seoDescription}
                    maxLength={155}
                    rows={3}
                    onChange={(e) => onSeoDescriptionChange(e.target.value)}
                    placeholder="Lubina (Dicentrarchus labrax): cómo reconocerla, dónde vive y cómo se pesca."
                    className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm resize-y"
                />
            </div>
            <div className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-foreground">Preguntas frecuentes</span>
                    <button
                        type="button"
                        className="px-3 py-1.5 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground bg-muted/50"
                        onClick={() => onFaqsChange([...faqs, { question: '', answer: '' }])}
                    >
                        Añadir pregunta
                    </button>
                </div>
                {faqs.length === 0 && (
                    <p className="text-xs text-muted-foreground">Sin preguntas. La ficha no mostrará el bloque FAQ.</p>
                )}
                {faqs.map((item, index) => (
                    <div key={index} className="space-y-2 rounded-lg border border-border/70 p-3">
                        <input
                            type="text"
                            value={item.question}
                            onChange={(e) => updateFaq(index, { question: e.target.value })}
                            placeholder="Pregunta"
                            aria-label={`Pregunta ${index + 1}`}
                            className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm"
                        />
                        <textarea
                            value={item.answer}
                            onChange={(e) => updateFaq(index, { answer: e.target.value })}
                            placeholder="Respuesta"
                            aria-label={`Respuesta ${index + 1}`}
                            rows={3}
                            className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm resize-y"
                        />
                        <button
                            type="button"
                            className="text-sm text-muted-foreground hover:text-foreground"
                            onClick={() => onFaqsChange(faqs.filter((_, i) => i !== index))}
                        >
                            Quitar
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}
